import { CheckCircle, Upload, X } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { imageService } from '../../services/imageService';
import type { ImageFolder } from '../../types/images';
import Button from '../../components/Button';
import InlineErrorDisplay from '../../components/InlineErrorDisplay';

interface ImageUploadProps {
    folder: ImageFolder;
    onUploadSuccess: (url: string) => void;
    onUploadError?: (error: string) => void;
    className?: string;
    showPreview?: boolean;
}

export default function ImageUpload({
    folder,
    onUploadSuccess,
    onUploadError,
    className = "",
    showPreview = true
}: ImageUploadProps) {
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [preview, setPreview] = useState<string | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [uploadSuccess, setUploadSuccess] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    }, []);

    const handleFileSelection = useCallback((file: File) => {
        setError(null);
        setUploadSuccess(false);

        // Validate file
        const validation = imageService.validateFile(file);
        if (!validation.isValid) {
            setError(validation.error || 'Invalid file');
            return;
        }

        setSelectedFile(file);

        // Create preview
        if (showPreview) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setPreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    }, [showPreview]);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            handleFileSelection(files[0]);
        }
    }, [handleFileSelection]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            handleFileSelection(files[0]);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) return;

        setIsUploading(true);
        setUploadProgress(0);
        setError(null);

        try {
            // Simulate upload progress
            const progressInterval = setInterval(() => {
                setUploadProgress(prev => {
                    if (prev >= 90) {
                        clearInterval(progressInterval);
                        return 90;
                    }
                    return prev + 10;
                });
            }, 100);

            const url = await imageService.uploadImage(selectedFile, folder);

            clearInterval(progressInterval);
            setUploadProgress(100);
            setUploadSuccess(true);

            setTimeout(() => {
                onUploadSuccess(url);
                resetComponent();
            }, 1000);

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Upload failed';
            setError(errorMessage);
            onUploadError?.(errorMessage);
        } finally {
            setIsUploading(false);
        }
    };

    const resetComponent = () => {
        setSelectedFile(null);
        setPreview(null);
        setUploadProgress(0);
        setError(null);
        setUploadSuccess(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveFile = () => {
        resetComponent();
    };

    return (
        <div className={`space-y-4 ${className}`}>
            {/* Upload Area */}
            {!selectedFile && (
                <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors duration-200 cursor-pointer
            ${isDragging
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                        }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <div className="text-sm text-gray-600">
                        <span className="font-medium text-blue-600 hover:text-blue-500">
                            Click to upload
                        </span>
                        {' '} or drag and drop
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                        JPG, PNG, WebP, SVG up to 5MB
                    </p>
                </div>
            )}

            {/* File Preview */}
            {selectedFile && showPreview && (
                <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start gap-4">
                        {preview && (
                            <div className="flex-shrink-0">
                                <img
                                    src={preview}
                                    alt="Preview"
                                    className="w-16 h-16 object-cover rounded border"
                                />
                            </div>
                        )}

                        <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                        {selectedFile.name}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        {(selectedFile.size / 1024).toFixed(1)} KB
                                    </p>
                                </div>

                                {!isUploading && !uploadSuccess && (
                                    <Button
                                        variant="ghost"
                                        onClick={handleRemoveFile}
                                    >
                                        <X size={16} />
                                    </Button>
                                )}
                            </div>

                            {/* Progress Bar */}
                            {isUploading && (
                                <div className="mt-2">
                                    <div className="bg-gray-200 rounded-full h-2">
                                        <div
                                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                            style={{ width: `${uploadProgress}%` }}
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Uploading... {uploadProgress}%
                                    </p>
                                </div>
                            )}

                            {/* Success State */}
                            {uploadSuccess && (
                                <div className="mt-2 flex items-center gap-1 text-green-600">
                                    <CheckCircle size={16} />
                                    <span className="text-sm">Upload successful!</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Error Display */}
            {error && (
                <InlineErrorDisplay
                    message={error}
                    onDismiss={() => setError(null)}
                />
            )}

            {/* Upload Button */}
            {selectedFile && !uploadSuccess && (
                <div className="flex gap-2">
                    <Button
                        variant="primary"
                        size="md"
                        onClick={handleUpload}
                        disabled={isUploading}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center gap-2"
                    >
                        {isUploading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                Uploading...
                            </>
                        ) : (
                            <>
                                <Upload size={16} />
                                Upload to {folder}
                            </>
                        )}
                    </Button>

                    <Button
                        variant="secondary"
                        size="md"
                        onClick={handleRemoveFile}
                        disabled={isUploading}
                        className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
                    >
                        Cancel
                    </Button>
                </div>
            )}

            {/* Hidden File Input */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,image/svg+xml"
                onChange={handleFileChange}
                className="hidden"
            />
        </div>
    );
} 